import EventEmitter from 'eventemitter3';
import { ExecutionResult, GraphQLError } from 'graphql';
import { Client, createClient as createGraphQLWSClient } from 'graphql-ws';
import * as jsoncParser from 'jsonc-parser';
import _ from 'lodash';
import moment from 'moment';
import * as monacoEditor from 'monaco-editor';
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Observer,
  OperationOptions,
  SubscriptionClient as TransportSubscriptionClient,
} from 'subscriptions-transport-ws';

import {
  OperationRequestBody,
  handleApolloHeadersAndExtraction,
  performOperation,
} from 'src/app/graph/explorerPage/hooks/useExplorerState/performOperation/performOperation';
import { ExplorerResultsTab } from 'src/app/graph/explorerPage/resultsPane/ExplorerResultsTab';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import {
  EXPLORER_SET_SOCKET_ERROR,
  EXPLORER_SET_SOCKET_STATUS,
  EXPLORER_SUBSCRIPTION_REQUEST,
  EXPLORER_SUBSCRIPTION_RESPONSE,
  EXPLORER_SUBSCRIPTION_TERMINATION,
  ExplorerSubscriptionResponse,
  IncomingEmbedMessageEvent,
  ResponseError,
  sendPostMessageFromEmbedToParent,
} from '../../helpers/postMessageHelpers';
import { JSONObject } from '../../helpers/types';

import type { PublicUrlConfirmation } from './useHasPublicUrlBeenConfirmed';

export type SubscriptionResponses = (
  | {
      timestamp: string;
      data: ExecutionResult;
      isError?: undefined;
      isSeparator?: undefined;
    }
  | {
      timestamp: string;
      data: Error | ResponseError | { errors: readonly GraphQLError[] };
      isError: true;
      isSeparator?: undefined;
    }
  | {
      timestamp: string;
      isSeparator: true;
    }
)[];

export type GraphQLSubscriptionLibrary =
  | 'subscriptions-transport-ws'
  | 'graphql-ws'
  | 'http-multipart';

export type GraphQLSubscriptionLibraryChoice =
  | GraphQLSubscriptionLibrary
  | 'auto-detect';

type HTTPMultipartParams = {
  includeCookies: boolean;
  requestQueryPlan: boolean;
  showTraces: boolean;
  abortSignalRef: React.MutableRefObject<AbortController | undefined>;
  requestConnectorsDebugging: boolean;
};

type HTTPMultipartClient = EventEmitter<
  'connected' | 'error' | 'disconnected'
> & {
  stopListeningCallback: (() => void) | undefined;
};

class SubscriptionClient<Protocol extends GraphQLSubscriptionLibrary> {
  protocol: Protocol;
  unsubscribeFunctions: Array<() => void> = [];
  url: string;
  headers: Record<string, string> | undefined;
  // Private variables
  private _multipartClient: HTTPMultipartClient | undefined;
  private _graphWsClient: Client | undefined;
  private _transportSubscriptionClient: undefined | TransportSubscriptionClient;

  constructor(
    url: string,
    headers: Record<string, string> | undefined,
    protocol: Protocol,
  ) {
    this.protocol = protocol;
    this.url = url;
    this.headers = headers;
  }

  public get graphWsClient(): Client {
    const client =
      this._graphWsClient ??
      createGraphQLWSClient({
        url: this.url,
        lazy: true,
        connectionParams: this.headers ?? {},
        keepAlive: 10_000,
      });
    this._graphWsClient = client;
    return client;
  }

  public get transportSubscriptionClient(): TransportSubscriptionClient {
    const client =
      this._transportSubscriptionClient ??
      new TransportSubscriptionClient(this.url, {
        reconnect: true,
        lazy: true,
        connectionParams: this.headers ?? {},
      });
    this._transportSubscriptionClient = client;
    return client;
  }

  public get multipartClient(): HTTPMultipartClient {
    const client =
      this._multipartClient ??
      Object.assign(
        new EventEmitter<'connected' | 'error' | 'disconnected'>(),
        {
          stopListeningCallback: undefined,
        },
      );
    this._multipartClient = client;
    return client;
  }

  onConnected(callback: () => void) {
    if (this.protocol === 'http-multipart') {
      this.multipartClient.on('connected', callback);
      return () => this.multipartClient.off('connected', callback);
    }
    if (this.protocol === 'graphql-ws') {
      return this.graphWsClient.on('connected', callback);
    }
    if (this.protocol === 'subscriptions-transport-ws') {
      return this.transportSubscriptionClient.onConnected(callback);
    }
    assertUnreachable(this.protocol);
  }
  onConnecting(callback: () => void) {
    if (this.protocol === 'http-multipart') {
      return;
    }
    if (this.protocol === 'graphql-ws') {
      return this.graphWsClient.on('connecting', callback);
    }
    if (this.protocol === 'subscriptions-transport-ws') {
      return this.transportSubscriptionClient.onConnecting(callback);
    }
    assertUnreachable(this.protocol);
  }
  onError(callback: Dispatch<SetStateAction<Error | undefined>>) {
    if (this.protocol === 'http-multipart') {
      this.multipartClient.on('error', callback);
      return () => this.multipartClient.off('error', callback);
    }
    if (this.protocol === 'graphql-ws') {
      return this.graphWsClient.on('error', (error) =>
        callback(error as Error),
      );
    }
    if (this.protocol === 'subscriptions-transport-ws') {
      return this.transportSubscriptionClient.onError(callback);
    }
    assertUnreachable(this.protocol);
  }
  onReconnecting(callback: () => void) {
    if (this.protocol === 'http-multipart') {
      return;
    }
    if (this.protocol === 'graphql-ws') {
      return;
    }
    if (this.protocol === 'subscriptions-transport-ws') {
      return this.transportSubscriptionClient.onReconnecting(callback);
    }
    assertUnreachable(this.protocol);
  }
  onReconnected(callback: () => void) {
    if (this.protocol === 'http-multipart') {
      return;
    }
    if (this.protocol === 'graphql-ws') {
      return;
    }
    if (this.protocol === 'subscriptions-transport-ws') {
      return this.transportSubscriptionClient.onReconnected(callback);
    }
    assertUnreachable(this.protocol);
  }
  onDisconnected(callback: () => void) {
    if (this.protocol === 'http-multipart') {
      this.multipartClient.on('disconnected', callback);
      return () => this.multipartClient.off('disconnected', callback);
    }
    if (this.protocol === 'graphql-ws') {
      return this.graphWsClient.on('closed', callback);
    }
    if (this.protocol === 'subscriptions-transport-ws') {
      return this.transportSubscriptionClient.onDisconnected(callback);
    }
    assertUnreachable(this.protocol);
  }

  dispose() {
    if (this.protocol === 'http-multipart') {
      this.multipartClient.stopListeningCallback?.();
      return;
    }
    if (this.protocol === 'graphql-ws') {
      return this.graphWsClient.dispose();
    }
    if (this.protocol === 'subscriptions-transport-ws') {
      return this.transportSubscriptionClient.close();
    }
    assertUnreachable(this.protocol);
  }

  request(
    params: OperationOptions & {
      query: string;
      variables: JSONObject;
      operationName: string | undefined;
      httpMultipartParams?: HTTPMultipartParams;
    },
  ) {
    return {
      subscribe: async (
        subscribeParams: Observer<ExecutionResult<JSONObject>>,
      ) => {
        if (this.protocol === 'http-multipart') {
          await performOperation({
            requestBody: params,
            processedHeaders: this.headers ?? {},
            includeCookies: params.httpMultipartParams?.includeCookies ?? false,
            requestQueryPlan:
              params.httpMultipartParams?.requestQueryPlan ?? false,
            showTraces: params.httpMultipartParams?.showTraces ?? false,
            url: this.url,
            abortSignal:
              params.httpMultipartParams?.abortSignalRef.current?.signal,
            requestConnectorsDebugging:
              params.httpMultipartParams?.requestConnectorsDebugging ?? false,
            onResponseUpdate: (response, shouldTerminate, unsubscribe) => {
              if (response.type === 'result') {
                if (response.body && shouldTerminate) {
                  this.multipartClient.emit('error', response.body.errors);
                  this.multipartClient.emit('disconnected');
                } else if (
                  response.body &&
                  isMinLengthArray(1, response.body.errors)
                ) {
                  this.multipartClient.emit('error', response.body.errors);
                }
                const firstResponseErrors = response.body?.errors?.[0];

                if (response.body?.data) {
                  subscribeParams.next?.(response.body.data);
                  this.multipartClient.emit('connected');
                } else if (firstResponseErrors) {
                  subscribeParams.error?.(firstResponseErrors);
                }
                if (
                  response.body &&
                  isMinLengthArray(1, response.body.errors)
                ) {
                  this.multipartClient.emit('error', response.body.errors);
                }

                this.multipartClient.stopListeningCallback = unsubscribe;
              }
            },
            isMultipartSubscription: true,
          });
          return;
        }
        if (this.protocol === 'graphql-ws') {
          this.unsubscribeFunctions.push(
            this.graphWsClient.subscribe(params, {
              ...subscribeParams,
              next: (data) =>
                subscribeParams.next?.(data as ExecutionResult<JSONObject>),
              error: (error) => subscribeParams.error?.(error as Error),
              complete: () => {},
            }),
          );
          return;
        }
        if (this.protocol === 'subscriptions-transport-ws') {
          return this.transportSubscriptionClient
            .request(params)
            .subscribe(subscribeParams);
        }
        assertUnreachable(this.protocol);
      },
    };
  }

  unsubscribeAll() {
    if (this.protocol === 'http-multipart') {
      this.multipartClient.stopListeningCallback?.();
      return;
    }
    if (this.protocol === 'graphql-ws') {
      this.unsubscribeFunctions.forEach((off) => {
        off();
      });
      this.unsubscribeFunctions = [];
      return;
    }

    if (this.protocol === 'subscriptions-transport-ws') {
      return this.transportSubscriptionClient.unsubscribeAll();
    }
    assertUnreachable(this.protocol);
  }
}

export type SocketStatus = 'disconnected' | 'connecting' | 'connected';

const SUBSCRIPTION_RESPONSES_LIMIT = 300;

export function isHttpMultipart(
  protocol: GraphQLSubscriptionLibraryChoice,
  graphURL: string | null,
) {
  if (protocol === 'http-multipart') return true;
  if (protocol === 'auto-detect') {
    const urlObj = new URL(graphURL ?? '');

    if (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') {
      return true;
    }
  }
  return false;
}

export const useSubscriptions = <
  ProtocolChoice extends GraphQLSubscriptionLibraryChoice,
>({
  hasPublicUrlBeenConfirmed,
  getInterpolatedHeadersAndVariables,
  variablesEditor,
  setResultsTab,
  subscriptionUrl,
  shouldPostMessageOperations,
  preflightScript,
  protocol,
  httpMultipartParams,
}: {
  hasPublicUrlBeenConfirmed: PublicUrlConfirmation;
  getInterpolatedHeadersAndVariables: (
    requestBody: OperationRequestBody,
  ) => Promise<{ headers: Record<string, string>; variables: JSONObject }>;
  variablesEditor: monacoEditor.editor.IStandaloneCodeEditor;
  setResultsTab: Dispatch<SetStateAction<ExplorerResultsTab>>;
  subscriptionUrl: string | undefined | null;
  shouldPostMessageOperations?: boolean | null;
  preflightScript: string | undefined;
  protocol: ProtocolChoice;
  httpMultipartParams: ProtocolChoice extends 'http-multipart'
    ? HTTPMultipartParams
    : undefined;
}) => {
  const [socketError, setSocketError] = useState<Error>();
  const [socketStatus, setSocketStatus] =
    useState<SocketStatus>('disconnected');
  const [isListening, setIsListening] = React.useState(false);
  const [currentSubscriptionDetails, setCurrentSubscriptionDetails] = useState<{
    operationName: string | undefined;
    body: string;
    variables: JSONObject;
    id: string;
    headers: Record<string, string>;
  }>();
  // In order to handle high volumes of responses, we store each subscription response immediately in a ref, but throttle react state updates (which would trigger rerender)
  const subscriptionResponseRef = useRef<SubscriptionResponses>([]);
  const [subscriptionResponse, setSubscriptionResponse] =
    useState<SubscriptionResponses>([]);

  const throttledSyncSubscriptionResponse = useMemo(() => {
    return _.throttle(() => {
      setSubscriptionResponse((current) => {
        const newResponses = [...subscriptionResponseRef.current, ...current];
        subscriptionResponseRef.current = [];
        if (newResponses.length > SUBSCRIPTION_RESPONSES_LIMIT) {
          newResponses.length = SUBSCRIPTION_RESPONSES_LIMIT;
        }
        return newResponses;
      });
    }, 60);
  }, []);

  const addSubscriptionResponse = useCallback(
    (response: SubscriptionResponses[number]) => {
      subscriptionResponseRef.current.unshift(response);
      throttledSyncSubscriptionResponse();
    },
    [throttledSyncSubscriptionResponse],
  );
  const clearSubscriptionResponses = useCallback(() => {
    subscriptionResponseRef.current.length = 0;
    setSubscriptionResponse([]);
  }, []);

  const supportsSubscriptionsTransportWS = useCallback(
    (url: string): Promise<boolean> =>
      new Promise((resolve) => {
        const client = new TransportSubscriptionClient(url, {
          lazy: false,
          reconnectionAttempts: 0,
          connectionParams: currentSubscriptionDetails?.headers ?? {},
        });
        client.onConnected(() => {
          resolve(true);
          client.close();
        });
        client.onDisconnected(() => {
          resolve(false);
        });
      }),
    [currentSubscriptionDetails?.headers],
  );

  const supportsGraphQLTransportWS = useCallback(
    (url: string): Promise<boolean> =>
      new Promise((resolve) => {
        const client = createGraphQLWSClient({
          url,
          retryAttempts: 0, // fail immediately
          lazy: false, // connect as soon as the client is created
          connectionParams: currentSubscriptionDetails?.headers ?? {},
          on: {
            closed: () => resolve(false), // connection rejected, probably not supported
            connected: () => {
              resolve(true); // connected = supported
              client.dispose(); // dispose after check
            },
          },
        });
      }),
    [currentSubscriptionDetails?.headers],
  );

  const autoDetectSubscriptionProtocol = useCallback(
    async (url: string) => {
      if (isHttpMultipart('auto-detect', url)) {
        return 'http-multipart';
      }
      const [subscriptionWsSupported, graphqlWsSupported] = await Promise.all([
        supportsSubscriptionsTransportWS(url),
        supportsGraphQLTransportWS(url),
      ]);
      if (graphqlWsSupported || subscriptionWsSupported) {
        return graphqlWsSupported ? 'graphql-ws' : 'subscriptions-transport-ws';
      } else {
        throw new Error(
          'Your subscription url is a websocket url, so your server must support graphql-ws or subscriptions-transport-ws protocol. If your server supports HTTP multipart subscriptions, change your subscription url to HTTP.',
        );
      }
    },
    [supportsGraphQLTransportWS, supportsSubscriptionsTransportWS],
  );

  const [client, setClient] = useState<
    SubscriptionClient<GraphQLSubscriptionLibrary> | undefined
  >();
  // The didClientFail ref is used to block the client from being created
  // multiple times in runSubscription. After it attempts to create the client
  // the first time, this is set to true to avoid loops with re-creating client.
  // The ref is used instead of state to prevent triggering extra re-renders.
  const didClientFail = useRef(false);

  useEffect(() => {
    didClientFail.current = true;
  }, [protocol, subscriptionUrl]);

  useEffect(() => {
    if (!client) {
      return;
    }
    const offs = [
      client.onError(setSocketError),
      client.onConnected(() => {
        didClientFail.current = false;
        setSocketError(undefined);
        setSocketStatus('connected');
      }),
      client.onReconnected(() => {
        didClientFail.current = false;
        setSocketError(undefined);
        setSocketStatus('connected');
      }),
      client.onConnecting(() => setSocketStatus('connecting')),
      client.onReconnecting(() => setSocketStatus('connecting')),
      client.onDisconnected(() => {
        didClientFail.current = true;
        setSocketStatus('disconnected');
      }),
    ];
    return () => {
      offs.forEach((off) => off?.());

      setSocketStatus('disconnected');
    };
  }, [client]);

  const stopListening = useCallback(() => {
    setIsListening(false);
    client?.dispose();

    if (shouldPostMessageOperations && currentSubscriptionDetails?.id) {
      sendPostMessageFromEmbedToParent({
        name: EXPLORER_SUBSCRIPTION_TERMINATION,
        operationId: currentSubscriptionDetails.id,
      });
    }
  }, [client, currentSubscriptionDetails, shouldPostMessageOperations]);

  // When client or currentSubscriptionDetails changes, we want to subscribe
  // to the given operation.
  useEffect(() => {
    if (!currentSubscriptionDetails || !isListening) {
      return;
    }

    if (shouldPostMessageOperations) {
      const onResponseUpdate = (response: ExplorerSubscriptionResponse) => {
        // Transport layer errors (as distinct from GraphQL or NetworkErrors),
        // these are fatal errors that should terminate the connection
        const graphQLErrors = response.data?.errors;
        if (
          response.data &&
          'shouldTerminate' in response.data &&
          response.data.shouldTerminate &&
          isMinLengthArray(1, graphQLErrors)
        ) {
          stopListening();
        }

        const error =
          // http multipart reponses
          response.data &&
          'payload' in response.data &&
          response.data.payload?.error
            ? response.data.payload.error
            : // websocket responses, or caught errors reported from the embed
              'error' in response
              ? response.error
              : undefined;

        if (error || isMinLengthArray(1, graphQLErrors)) {
          addSubscriptionResponse({
            timestamp: moment().format('kk:mm:ss'),
            data: error ?? { errors: graphQLErrors ?? [] },
            isError: true,
          });
        }

        const data = response.data
          ? 'payload' in response.data
            ? response.data.payload
            : // `shouldTerminate` is a field added by us to signal
              // that we should stop listening, we don't want to expose
              // it to users, but we need to send it via PM with the response
              _.omit(response.data, 'shouldTerminate')
          : null;

        // for `error` in websockets, we don't return data,
        // but we should have handled that above
        if (data) {
          addSubscriptionResponse({
            isError: undefined,
            timestamp: moment().format('kk:mm:ss'),
            data: data as ExecutionResult,
          });
        }
      };
      const handleSubscriptionResponsePostMessage = (
        event: IncomingEmbedMessageEvent,
      ) => {
        if (
          event.data.name === EXPLORER_SUBSCRIPTION_RESPONSE &&
          event.data.operationId === currentSubscriptionDetails.id
        ) {
          const response = event.data.response;

          onResponseUpdate(response);
        }
      };

      const handleSetSocketErrorPostMessage = (
        event: IncomingEmbedMessageEvent,
      ) => {
        if (event.data.name === EXPLORER_SET_SOCKET_ERROR) {
          setSocketError(event.data.error);
        }
      };

      const handleSetSocketStatusPostMessage = (
        event: IncomingEmbedMessageEvent,
      ) => {
        if (event.data.name === EXPLORER_SET_SOCKET_STATUS) {
          setSocketStatus(event.data.status);
        }
      };

      window.addEventListener('message', handleSubscriptionResponsePostMessage);
      window.addEventListener('message', handleSetSocketErrorPostMessage);
      window.addEventListener('message', handleSetSocketStatusPostMessage);
      return () => {
        window.removeEventListener(
          'message',
          handleSubscriptionResponsePostMessage,
        );
        window.removeEventListener('message', handleSetSocketErrorPostMessage);
        window.removeEventListener('message', handleSetSocketStatusPostMessage);
      };
    }

    let isCancelled = false;
    const runSubscription = async () => {
      if (!subscriptionUrl) {
        setSocketError(new Error('must set subscription URL in settings tab'));
        return;
      }

      if (!client && !didClientFail.current) {
        setSocketError(undefined);
        const supportedProtocol: GraphQLSubscriptionLibrary | void =
          protocol === 'auto-detect'
            ? await autoDetectSubscriptionProtocol(subscriptionUrl)
                .then((validProtocol) => validProtocol)
                .catch((e) => setSocketError(e))
            : protocol;

        if (supportedProtocol) {
          setClient(
            new SubscriptionClient(
              subscriptionUrl,
              currentSubscriptionDetails.headers ?? {},
              supportedProtocol,
            ),
          );
        }
        return;
      }

      if (!client || isCancelled) return;

      const { operationName, body, variables } = currentSubscriptionDetails;
      client
        .request({
          query: body,
          operationName,
          variables,
          httpMultipartParams,
        })
        .subscribe({
          next(data) {
            addSubscriptionResponse({
              timestamp: moment().format('kk:mm:ss'),
              data,
              isError: undefined,
            });
          },
          error: (error) => {
            addSubscriptionResponse({
              timestamp: moment().format('kk:mm:ss'),
              data: error,
              isError: true,
            });
          },
        });
    };

    runSubscription();

    return () => {
      if (client) client.unsubscribeAll();
      isCancelled = true;
    };
  }, [
    autoDetectSubscriptionProtocol,
    client,
    currentSubscriptionDetails,
    httpMultipartParams,
    isListening,
    protocol,
    shouldPostMessageOperations,
    stopListening,
    subscriptionUrl,
    supportsGraphQLTransportWS,
    supportsSubscriptionsTransportWS,
    addSubscriptionResponse,
  ]);

  // Keep a reference to the current operation name so we can keep or
  // clear old results when starting a new subscription
  const subscriptionName = currentSubscriptionDetails?.operationName;
  const currentOperationName = useRef(subscriptionName);
  currentOperationName.current = subscriptionName;

  const submitSubscription = useCallback(
    async ({
      operationName,
      body,
    }: {
      operationName: string | undefined;
      body: string;
    }) => {
      const stableSubscriptionId = _.uniqueId();
      if (
        !(await hasPublicUrlBeenConfirmed({
          url: subscriptionUrl,
          preflightScript,
          shouldPrompt: true,
          canApproveSingleRun: true,
          isSubscription: true,
        }))
      ) {
        return;
      }

      setResultsTab('Response');
      // If this is a new operation, clear existing results, otherwise
      // leave them around as a previous set to be shown greyed out
      if (operationName === currentOperationName.current) {
        addSubscriptionResponse({
          timestamp: moment().format('kk:mm:ss'),
          isSeparator: true,
        });
      } else {
        clearSubscriptionResponses();
      }

      let unprocessedVariables: JSONObject;
      try {
        unprocessedVariables = jsoncParser.parse(variablesEditor.getValue());
      } catch {
        unprocessedVariables = {};
      }
      if (didClientFail.current) {
        setClient(undefined);
      }
      didClientFail.current = false;
      const { headers: refreshedHeaders, variables: processedVariables } =
        await getInterpolatedHeadersAndVariables({
          operationName,
          query: body,
          variables: unprocessedVariables,
        });
      setCurrentSubscriptionDetails((current) => {
        const nextDetails = {
          operationName,
          body,
          variables: processedVariables,
          id: stableSubscriptionId,
          headers: refreshedHeaders,
        };
        // clear client if headers are not equal so we re initialize with the new values.
        if (!_.isEqual(refreshedHeaders, current?.headers)) {
          setClient(undefined);
        }
        return nextDetails;
      });
      setIsListening(true);

      if (shouldPostMessageOperations) {
        const sendSubscriptionRequestToEmbed = async () => {
          setSocketError(undefined);

          if (!subscriptionUrl) {
            setSocketError(
              new Error('must set subscription URL in settings tab'),
            );
            return;
          }

          const supportedProtocol: GraphQLSubscriptionLibrary | void =
            protocol === 'auto-detect'
              ? await autoDetectSubscriptionProtocol(subscriptionUrl)
                  .then((validProtocol) => validProtocol)
                  .catch((e) => setSocketError(e))
              : protocol;

          if (supportedProtocol) {
            sendPostMessageFromEmbedToParent({
              name: EXPLORER_SUBSCRIPTION_REQUEST,
              operationId: stableSubscriptionId,
              operation: body,
              variables: processedVariables,
              headers:
                supportedProtocol === 'http-multipart' && httpMultipartParams
                  ? handleApolloHeadersAndExtraction({
                      processedHeaders: refreshedHeaders,
                      isMultipartSubscription: true,
                      operationString: body,
                      addContentType: true,
                      ...httpMultipartParams,
                    }).processedHeadersWithApolloHeaders
                  : refreshedHeaders,
              operationName,
              subscriptionUrl,
              protocol: supportedProtocol,
              // only used for multipart protocol
              httpMultipartParams: {
                includeCookies: httpMultipartParams?.includeCookies,
              },
            });
          }
        };

        sendSubscriptionRequestToEmbed();
      }
    },
    [
      hasPublicUrlBeenConfirmed,
      subscriptionUrl,
      preflightScript,
      setResultsTab,
      getInterpolatedHeadersAndVariables,
      shouldPostMessageOperations,
      variablesEditor,
      protocol,
      autoDetectSubscriptionProtocol,
      httpMultipartParams,
      addSubscriptionResponse,
      clearSubscriptionResponses,
    ],
  );

  const clearSubscription = useCallback(() => {
    stopListening();
    clearSubscriptionResponses();
    setCurrentSubscriptionDetails(undefined);
  }, [clearSubscriptionResponses, stopListening]);

  return useMemo(
    () => ({
      socketError,
      socketStatus,
      submitSubscription,
      isListening,
      subscriptionResponse,
      stopListening,
      clearSubscription,
    }),
    [
      socketError,
      socketStatus,
      submitSubscription,
      isListening,
      subscriptionResponse,
      stopListening,
      clearSubscription,
    ],
  );
};
